/** Клавиатура цифр.
 *
 * Под каждой цифрой — сколько её осталось расставить. Полностью
 * расставленная гаснет до 40%, но остаётся на месте: убрать клавишу значит
 * перестроить ряд прямо под пальцем.
 *
 * Долгое нажатие ставит заметку, не трогая переключатель режима: именно
 * так играют одной рукой, когда нужен один кандидат, а не разметка всей
 * доски. В режиме заметок жесты меняются местами.
 */

import { countDigits } from '../game/board'
import { useLongPress } from '../game/useLongPress'
import { useNarrowScreen } from '../game/useNarrowScreen'
import { t } from '../i18n'

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const PER_DIGIT = 9

export interface KeypadProps {
  board: string
  /** Режим заметок: короткое нажатие уводит цифру в карандаш. */
  notesMode: boolean
  /** Выбранная цифра в режиме «сначала цифра, потом клетки». */
  selectedDigit?: number | null
  disabled?: boolean
  onDigit: (digit: number) => void
  onNote: (digit: number) => void
}

export function Keypad({
  board,
  notesMode,
  selectedDigit = null,
  disabled = false,
  onDigit,
  onNote,
}: KeypadProps) {
  const counts = countDigits(board)
  const narrow = useNarrowScreen()
  const rows = narrow ? [DIGITS.slice(0, 5), DIGITS.slice(5)] : [DIGITS]

  return (
    <div className={`keypad ${notesMode ? 'keypad--notes' : ''}`.trim()}>
      {notesMode && <div className="keypad__hint">{t('game.notesHint')}</div>}
      {rows.map((row, index) => (
        <div className="keypad__row" key={index}>
          {row.map((digit) => (
            <Key
              key={digit}
              digit={digit}
              left={PER_DIGIT - counts[digit]}
              selected={selectedDigit === digit}
              disabled={disabled}
              onPress={() => (notesMode ? onNote(digit) : onDigit(digit))}
              onLongPress={() => (notesMode ? onDigit(digit) : onNote(digit))}
            />
          ))}
          {/* Девятая позиция второй строки остаётся пустой, чтобы цифры
              стояли ровной решёткой, а не съезжали к краю. */}
          {narrow && index === 1 && <div className="keypad__key keypad__key--filler" />}
        </div>
      ))}
    </div>
  )
}

interface KeyProps {
  digit: number
  left: number
  selected: boolean
  disabled: boolean
  onPress: () => void
  onLongPress: () => void
}

function Key({ digit, left, selected, disabled, onPress, onLongPress }: KeyProps) {
  const gestures = useLongPress(onPress, onLongPress)

  const classes = ['keypad__key']
  if (left <= 0) classes.push('keypad__key--done')
  if (selected) classes.push('keypad__key--selected')

  return (
    <button
      type="button"
      className={classes.join(' ')}
      disabled={disabled}
      aria-label={String(digit)}
      {...gestures}
    >
      <span className="keypad__digit">{digit}</span>
      <span className="keypad__left num">{Math.max(0, left)}</span>
    </button>
  )
}
